import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Typography,
  Switch,
  Button,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import {
  GET_USER_METRIC_PERMISSIONS_BY_USER_ID,
  GET_MY_METRIC_PERMISSIONS,
} from 'src/utils/gql/queries/userMetricPermission';
import {
  GET_SHOPIFY_METRICS,
  GET_FACEBOOK_METRICS,
  GET_GOOGLE_ADS_METRICS,
  GET_GOOGLE_ANALYTICS_METRICS,
} from 'src/utils/gql/queries';
import {
  UPDATE_MULTIPLE_METRIC_PERMISSIONS,
  UPDATE_MY_METRIC_PERMISSIONS,
} from 'src/utils/gql/mutations/userMetricPermission';
import { ConnectorType, UserMetricPermission, User } from 'src/utils/gql/types';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import { useAccount } from 'src/providers/account/hook/useAccount';
import { useIsAdmin } from 'src/hooks/useIsAdmin';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import { ReactComponent as FacebookIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import palette from 'src/theme/palette';

// Styled components per il design personalizzato
const PermissionsContainer = styled(Box)({
  backgroundColor: palette.background.paper,
  borderRadius: '8px',
  padding: '32px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  maxWidth: '800px',
  margin: '0 auto',
});

const HeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '32px',
});

const BackButton = styled(IconButton)({
  color: palette.text.secondary,
  '&:hover': {
    color: palette.primary.main,
  },
});

const UserInfo = styled(Box)({
  flex: 1,
});

const UserName = styled(Typography)({
  color: palette.text.primary,
  fontWeight: 600,
  fontSize: '20px',
});

const UserEmail = styled(Typography)({
  color: palette.text.secondary,
  fontSize: '14px',
});

const ConnectorHeader = styled(Box)({
  backgroundColor: palette.grey[100],
  color: palette.text.primary,
  padding: '16px 20px',
  borderRadius: '6px',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontWeight: 600,
  border: `1px solid ${palette.grey[200]}`,
});

const ConnectorIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  '& svg': {
    width: '20px',
    height: '20px',
  },
});

const MetricsList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const MetricItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  border: `1px solid ${palette.grey[200]}`,
  borderRadius: '6px',
  backgroundColor: palette.background.paper,
  '&:hover': {
    backgroundColor: palette.grey[100],
  },
});

const MetricName = styled(Typography)({
  color: palette.text.primary,
  fontWeight: 500,
  fontSize: '14px',
});

const CustomSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: palette.primary.main,
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: palette.primary.main,
  },
});

const SaveButton = styled(Button)({
  backgroundColor: palette.primary.main,
  color: palette.primary.contrastText,
  fontWeight: 600,
  borderRadius: '6px',
  padding: '12px 24px',
  textTransform: 'none',
  marginTop: '24px',
  '&:hover': {
    backgroundColor: palette.primary.light,
  },
  '&:disabled': {
    backgroundColor: palette.grey[300],
    color: palette.text.disabled,
  },
});

const CustomTabs = styled(Tabs)({
  '& .MuiTab-root': {
    color: palette.text.secondary,
    fontWeight: 500,
    textTransform: 'none',
    fontSize: '14px',
    minWidth: 'auto',
    padding: '12px 24px',
    '&.Mui-selected': {
      color: palette.text.primary,
      fontWeight: 600,
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: palette.text.primary,
    height: '2px',
  },
});

const CustomTab = styled(Tab)({
  '&.MuiTab-root': {
    color: palette.text.secondary,
    fontWeight: 500,
    textTransform: 'none',
    fontSize: '14px',
    minWidth: 'auto',
    padding: '12px 24px',
    '&.Mui-selected': {
      color: palette.text.primary,
      fontWeight: 600,
    },
  },
});

const RoleChip = styled(Chip)(({ isAdmin }: { isAdmin: boolean }) => ({
  backgroundColor: isAdmin ? palette.warning.light : palette.success.light,
  color: palette.text.primary,
  fontWeight: 500,
  borderRadius: '4px',
  padding: '4px 12px',
}));

// Funzione per ottenere le metriche dal backend invece che dal context
const useBackendConnectorMetrics = (connectorType: ConnectorType) => {
  const { data: shopifyMetricsData } = useQuery(GET_SHOPIFY_METRICS, {
    skip: connectorType !== ConnectorType.SHOPIFY,
  });

  const { data: facebookMetricsData } = useQuery(GET_FACEBOOK_METRICS, {
    skip: connectorType !== ConnectorType.FACEBOOK,
  });

  const { data: googleAdsMetricsData } = useQuery(GET_GOOGLE_ADS_METRICS, {
    skip: connectorType !== ConnectorType.GOOGLE_ADS,
  });

  const { data: googleAnalyticsMetricsData } = useQuery(
    GET_GOOGLE_ANALYTICS_METRICS,
    {
      skip: connectorType !== ConnectorType.GOOGlE_ANALYTICS4,
    },
  );

  return useMemo(() => {
    switch (connectorType) {
      case ConnectorType.SHOPIFY:
        return (
          shopifyMetricsData?.getShopifyMetrics?.metrics?.map(
            (metric: any) => ({
              key: metric.key,
              name: metric.label,
            }),
          ) || []
        );
      case ConnectorType.FACEBOOK:
        return (
          facebookMetricsData?.getFacebookMetrics?.metrics?.map(
            (metric: any) => ({
              key: metric.key,
              name: metric.label,
            }),
          ) || []
        );
      case ConnectorType.GOOGLE_ADS:
        return (
          googleAdsMetricsData?.getGoogleAdsMetricsList?.metrics?.map(
            (metric: any) => ({
              key: metric.key,
              name: metric.label,
            }),
          ) || []
        );
      case ConnectorType.GOOGlE_ANALYTICS4:
        return (
          googleAnalyticsMetricsData?.getGoogleAnalyticsMetrics?.metrics?.map(
            (metric: any) => ({
              key: metric.key,
              name: metric.label,
            }),
          ) || []
        );
      default:
        return [];
    }
  }, [
    connectorType,
    shopifyMetricsData,
    facebookMetricsData,
    googleAdsMetricsData,
    googleAnalyticsMetricsData,
  ]);
};

const getConnectorLabel = (type: ConnectorType) => {
  switch (type) {
    case ConnectorType.SHOPIFY:
      return 'SHOPIFY';
    case ConnectorType.FACEBOOK:
      return 'FACEBOOK';
    case ConnectorType.GOOGLE_ADS:
      return 'GOOGLE ADS';
    case ConnectorType.GOOGlE_ANALYTICS4:
      return 'GOOGLE ANALYTICS';
    default:
      return type;
  }
};

const getConnectorIcon = (type: ConnectorType) => {
  switch (type) {
    case ConnectorType.SHOPIFY:
      return <ShopifyIcon />;
    case ConnectorType.FACEBOOK:
      return <FacebookIcon />;
    case ConnectorType.GOOGLE_ADS:
      return <GoogleAdsIcon />;
    case ConnectorType.GOOGlE_ANALYTICS4:
      return <GoogleAnalyticsIcon />;
    default:
      return null;
  }
};

interface UserMetricPermissionsProps {
  user: User;
  onBack: () => void;
}

export const UserMetricPermissions: React.FC<UserMetricPermissionsProps> = ({
  user,
  onBack,
}) => {
  const snackbar = useSnackbar();
  const { user: currentUser } = useAccount();
  const isAdmin = useIsAdmin();
  const [selectedConnector, setSelectedConnector] = useState<ConnectorType>(
    ConnectorType.SHOPIFY,
  );
  const [userPermissions, setUserPermissions] = useState<
    Record<string, boolean>
  >({});
  const [hasChanges, setHasChanges] = useState(false);

  // Determine if current user is viewing their own permissions
  const isViewingOwnPermissions = currentUser?.id === user.id;

  // Usa query diversa in base al caso
  const {
    data: permissionsData,
    loading: loadingPermissions,
    refetch,
  } = useQuery(
    isViewingOwnPermissions
      ? GET_MY_METRIC_PERMISSIONS
      : GET_USER_METRIC_PERMISSIONS_BY_USER_ID,
    {
      variables: isViewingOwnPermissions ? {} : { userId: user.id },
      onError: (error) => {
        console.error('❌ Errore query permessi:', error);
        snackbar(error.message);
      },
      onCompleted: (data) => {
        console.log('✅ Query permessi completata:', data);
      },
      fetchPolicy: 'network-only', // Forza il refetch ogni volta
    },
  );

  // Usa mutazione diversa in base al caso
  const [updatePermissions, { loading: updating }] = useMutation(
    isViewingOwnPermissions
      ? UPDATE_MY_METRIC_PERMISSIONS
      : UPDATE_MULTIPLE_METRIC_PERMISSIONS,
    {
      onCompleted: (data) => {
        const message = isViewingOwnPermissions
          ? 'Your permissions have been updated successfully!'
          : 'Permissions updated successfully!';
        snackbar(message, 'success');
        setHasChanges(false);
        refetch(); // Ricarica i dati
      },
      onError: (error) => {
        console.error('❌ Errore aggiornamento permessi:', error);
        snackbar(error.message);
      },
    },
  );

  const permissions =
    permissionsData?.getMyMetricPermissions ||
    permissionsData?.getUserMetricPermissionsByUserId ||
    [];
  const connectorMetrics = useBackendConnectorMetrics(selectedConnector);

  // Organizza i permessi per metrica con logging migliorato
  React.useEffect(() => {
    if (connectorMetrics.length === 0) return;
    const permissionsMap: Record<string, boolean> = {};
    // Riduco il logging per evitare spam
    console.log('🔄 Aggiornamento permessi per:', selectedConnector);
    connectorMetrics.forEach((metric: { key: string; name: string }) => {
      const permission = permissions.find((p: UserMetricPermission) => {
        const userMatch = isViewingOwnPermissions
          ? true
          : p.user?.id === user.id;
        const metricMatch = p.metricKey === metric.key;
        const connectorMatch = p.connectorType === selectedConnector;
        return userMatch && metricMatch && connectorMatch;
      });
      permissionsMap[metric.key] = permission?.isEnabled ?? true;
    });
    setUserPermissions(permissionsMap);
  }, [permissions, selectedConnector, user.id, isViewingOwnPermissions]);

  const handlePermissionChange = (metricKey: string, enabled: boolean) => {
    console.log('🔍 Cambio permesso:', {
      metricKey,
      enabled,
      currentPermissions: userPermissions,
    });

    setUserPermissions((prev) => ({
      ...prev,
      [metricKey]: enabled,
    }));
    setHasChanges(true);
  };

  const handleSavePermissions = () => {
    const enabledMetrics = Object.entries(userPermissions)
      .filter(([_, enabled]) => enabled)
      .map(([key, _]) => key);

    console.log('🔍 Salvataggio permessi:', {
      isViewingOwnPermissions,
      selectedConnector,
      enabledMetrics,
      userPermissions,
      totalMetrics: connectorMetrics.length,
    });

    const variables = isViewingOwnPermissions
      ? {
          permissionsData: {
            connectorType: selectedConnector,
            enabledMetrics,
          },
        }
      : {
          permissionsData: {
            userId: user.id,
            connectorType: selectedConnector,
            enabledMetrics,
          },
        };

    console.log('🔍 Variabili mutazione:', variables);
    updatePermissions({ variables });
  };

  const handleConnectorChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    const connectors = Object.values(ConnectorType);
    setSelectedConnector(connectors[newValue]);
    setHasChanges(false); // Reset changes quando si cambia connettore
  };

  if (loadingPermissions) {
    return (
      <PermissionsContainer>
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      </PermissionsContainer>
    );
  }

  return (
    <PermissionsContainer>
      <HeaderContainer>
        <BackButton onClick={onBack}>
          <ArrowBackIcon />
        </BackButton>
        <UserInfo>
          <UserName>
            {user.firstName} {user.lastName}
          </UserName>
          <UserEmail>{user.email}</UserEmail>
        </UserInfo>
        <RoleChip
          label={
            user.role === 'admin'
              ? 'Admin'
              : user.role === 'company_admin'
                ? 'Company Admin'
                : 'User'
          }
          size="small"
          isAdmin={user.role === 'admin' || user.role === 'company_admin'}
        />
      </HeaderContainer>

      <Box sx={{ mb: 3 }}>
        <CustomTabs
          value={Object.values(ConnectorType).indexOf(selectedConnector)}
          onChange={handleConnectorChange}
          aria-label="connector tabs"
        >
          {Object.values(ConnectorType).map((connector) => (
            <CustomTab key={connector} label={getConnectorLabel(connector)} />
          ))}
        </CustomTabs>
      </Box>

      <ConnectorHeader>
        <ConnectorIcon>{getConnectorIcon(selectedConnector)}</ConnectorIcon>
        {getConnectorLabel(selectedConnector)}
      </ConnectorHeader>

      <MetricsList>
        {connectorMetrics.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No metrics available for this connector
            </Typography>
          </Box>
        ) : (
          connectorMetrics.map((metric: { key: string; name: string }) => (
            <MetricItem key={metric.key}>
              <MetricName>{metric.name}</MetricName>
              <CustomSwitch
                checked={userPermissions[metric.key] ?? true}
                onChange={(e) =>
                  handlePermissionChange(metric.key, e.target.checked)
                }
                disabled={updating}
              />
            </MetricItem>
          ))
        )}
      </MetricsList>

      <SaveButton
        variant="contained"
        fullWidth
        onClick={handleSavePermissions}
        disabled={updating || !hasChanges}
      >
        {updating ? (
          <CircularProgress size={24} color="inherit" />
        ) : isViewingOwnPermissions ? (
          'Save My Permissions'
        ) : (
          'Save Permissions'
        )}
      </SaveButton>
    </PermissionsContainer>
  );
};
